import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  CircularProgress,
  Dialog,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';

import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';

import ApiService from '../../../common/ApiCalls';
import NoImagePlaceholder from '../NoImage';
import { useQuickView } from '../../hooks/use-quick-view.hook';
import { useSettingsSelector } from '../../store/settings';

import './carousel.scss';

const AssetImageCarousel = ({ assetId, addressCoords }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { getGoogleStreetViewImage } = useQuickView();
  const { isGoogleImagePull } = useSettingsSelector();

  const swiperRef = useRef(null);

  const [allImages, setAllImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedImg, setSelectedImg] = useState(null);

  const loadImages = useCallback(async () => {
    const res = await ApiService().getImageByAssetId(assetId);
    const imgs = res?.data?.data?.images ?? [];

    if (!imgs?.length && addressCoords && isGoogleImagePull) {
      const url = getGoogleStreetViewImage(addressCoords);

      imgs.push({
        id: 'street-view',
        url: url
      });
    }
    setAllImages(imgs);

    return imgs;
  }, [assetId, isGoogleImagePull]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadImages();
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loadImages]);

  if (loading) return <CircularProgress />;

  return (
    <>
      <div className="asset-carousel-compact asset-carousel--compact">
        {allImages.length ? (
          <Swiper
            className="asset-carousel__swiper"
            modules={[Pagination]}
            grabCursor
            centeredSlides
            watchSlidesProgress={true}
            slidesPerView={1}
            initialSlide={0}
            onSwiper={(s) => (swiperRef.current = s)}
            pagination={{ clickable: true }}
          >
            {allImages.map((img) => (
              <SwiperSlide key={img.id}>
                <img
                  src={img.url}
                  alt=""
                  onClick={() => setSelectedImg(img.url)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div style={{ height: 200 }}>
            <NoImagePlaceholder variant="compact" />
          </div>
        )}

        <Dialog
          open={Boolean(selectedImg)}
          onClose={() => setSelectedImg(null)}
          fullScreen={fullScreen}
          PaperProps={{ style: { background: '#000' } }}
        >
          <IconButton
            className="asset-carousel__close-btn"
            onClick={() => setSelectedImg(null)}
          >
            <CloseIcon />
          </IconButton>
          {selectedImg && (
            <img
              src={selectedImg}
              alt="preview"
              className="asset-carousel__dialog-img"
            />
          )}
        </Dialog>
      </div>
    </>
  );
};

AssetImageCarousel.propTypes = {
  assetId: PropTypes.string,
  addressCoords: PropTypes.string
};

export default AssetImageCarousel;
